import { useToast } from "@qubit/autoparts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setClientConfig } from "~/app/app.slice";
import { ClientConfig } from "~/config/clientConfig";
import { setSelectedAndonLayoutName } from "~/features/andon/andon.slice";
import {
  AndonBoardTextLayoutItem,
  AndonGridCell,
  BoardDimensions,
  BoardLayout,
  BoardTextLabelFields,
  BorderSide,
  EMPTY_WORKING_LAYOUT,
  WorkingLayout,
  WorkstationWithFulfillmentCenter
} from "~/features/andon/andon.types";
import {
  addWorkstationsToBoard,
  fromWorkingLayout,
  calculateMinAndonBoardDimensions,
  isTextLayoutItem,
  isWorkstationLayoutItem,
  layoutsAreEqual,
  toWorkingLayout
} from "~/features/andon/andon.utils";
import {
  applyBoardDimensionChange,
  applyCellBordersToWorkingLayout,
  createTextLayoutItem,
  createWorkstationLayoutItem,
  findClosestEmptyBoardPosition,
  replaceWorkstationOnLayout,
  updateTextLayoutItem,
  upsertSavedBoardLayout
} from "~/features/andon/andonBoard/andonBoardLayout.utils";
import { WorkstationSummaryDto } from "~/types/api";

export type AndonBoardEditSelectionBridge = {
  closeEditCellMenu: () => void;
  selectedGridIndex: string | null;
  setSelectedGridIndex: (index: string | null) => void;
  editCellMenuAnchorPosition: { left: number; top: number } | null;
  borderTargetCells: AndonGridCell[];
};

/** Layout mutations read selection via ref so hooks can stay split without a cycle. */
type UseAndonBoardLayoutParams = {
  editSelectionRef: React.MutableRefObject<AndonBoardEditSelectionBridge>;
  boardWidth?: number;
  boardHeight?: number;
  workstationsRef: React.MutableRefObject<WorkstationSummaryDto[] | undefined>;
  siteFulfillmentCenterId?: string | null;
  an_boardLayouts: BoardLayout[];
  selectedLayoutName: string | null;
  clientConfig: ClientConfig;
  saveConfig: (config: ClientConfig) => Promise<void>;
};

export function useAndonBoardLayout({
  editSelectionRef,
  boardWidth,
  boardHeight,
  workstationsRef,
  siteFulfillmentCenterId,
  an_boardLayouts,
  selectedLayoutName,
  clientConfig,
  saveConfig
}: UseAndonBoardLayoutParams) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { errorToast } = useToast();

  const [textLabelModalOpen, setTextLabelModalOpen] = useState(false);
  const [editingTextLabel, setEditingTextLabel] =
    useState<BoardTextLabelFields | null>(null);
  const pendingTextLabelAnchorRef = useRef<{
    left: number;
    top: number;
  } | null>(null);
  const [workingLayout, setWorkingLayout] =
    useState<WorkingLayout>(EMPTY_WORKING_LAYOUT);
  const [savedLayout, setSavedLayout] = useState<WorkingLayout | null>(null);
  const {
    layout: workingLayoutItems,
    dimensions: boardDimensions,
    scale: boardScale,
    displayLines: boardDisplayLines,
    cellBorders,
    name: workingLayoutName,
    fulfillmentCenterIds: workingLayoutFcIds
  } = workingLayout;

  const [loadedLayoutName, setLoadedLayoutName] = useState<string | null>(null);
  const boardLayoutNamesKey = an_boardLayouts
    .map((layout) => layout.name)
    .join("\0");
  const [prevSelectedLayoutName, setPrevSelectedLayoutName] = useState<
    string | null | undefined
  >(undefined);
  const [prevBoardLayoutNamesKey, setPrevBoardLayoutNamesKey] = useState<
    string | undefined
  >(undefined);
  const shouldClearSelectedLayoutNameRef = useRef(false);

  if (
    selectedLayoutName !== prevSelectedLayoutName ||
    boardLayoutNamesKey !== prevBoardLayoutNamesKey
  ) {
    setPrevSelectedLayoutName(selectedLayoutName);
    setPrevBoardLayoutNamesKey(boardLayoutNamesKey);

    if (!selectedLayoutName) {
      setLoadedLayoutName(null);
      setSavedLayout(null);
    } else if (selectedLayoutName === loadedLayoutName) {
      const selectedLayoutStillExists = an_boardLayouts.some(
        (layout) => layout.name === selectedLayoutName
      );
      if (!selectedLayoutStillExists) {
        setLoadedLayoutName(null);
        setWorkingLayout(EMPTY_WORKING_LAYOUT);
        setSavedLayout(null);
        shouldClearSelectedLayoutNameRef.current = true;
      }
    } else {
      const savedLayoutConfig = an_boardLayouts.find(
        (layout) => layout.name === selectedLayoutName
      );
      if (savedLayoutConfig) {
        const nextWorkingLayout = toWorkingLayout(
          savedLayoutConfig,
          siteFulfillmentCenterId
        );
        setWorkingLayout(nextWorkingLayout);
        setSavedLayout(nextWorkingLayout);
        setLoadedLayoutName(selectedLayoutName);
      } else {
        setSavedLayout(null);
      }
    }
  }

  useEffect(() => {
    if (!shouldClearSelectedLayoutNameRef.current) return;
    shouldClearSelectedLayoutNameRef.current = false;
    dispatch(setSelectedAndonLayoutName(null));
  });

  const hasLayoutChanges = (): boolean => {
    if (!savedLayout) return workingLayout.layout.length > 0;

    return !layoutsAreEqual(workingLayout, savedLayout);
  };

  const loadWorkingLayout = useCallback(
    (saved: BoardLayout) => {
      const nextWorkingLayout = toWorkingLayout(saved, siteFulfillmentCenterId);

      setWorkingLayout(nextWorkingLayout);
      setSavedLayout(nextWorkingLayout);
      setLoadedLayoutName(saved.name);
      dispatch(setSelectedAndonLayoutName(saved.name));
    },
    [dispatch, siteFulfillmentCenterId]
  );
  const handleDimensionChange = (columns: number, rows: number) => {
    setWorkingLayout((currentWorkingLayout) =>
      applyBoardDimensionChange(currentWorkingLayout, columns, rows)
    );
  };

  const handleSaveLayout = async (layoutName: string, isRename: boolean) => {
    const newLayout = fromWorkingLayout(workingLayout, layoutName);
    const updatedLayouts = upsertSavedBoardLayout(
      Array.isArray(an_boardLayouts) ? an_boardLayouts : [],
      newLayout,
      { isRename, originalName: savedLayout?.name }
    );
    const configToSave = {
      ...clientConfig,
      an_boardLayouts: updatedLayouts
    };

    await saveConfig(configToSave);

    dispatch(setClientConfig(configToSave));
    const nextWorkingLayout = toWorkingLayout(
      newLayout,
      siteFulfillmentCenterId
    );
    setWorkingLayout(nextWorkingLayout);
    setSavedLayout(nextWorkingLayout);
    setLoadedLayoutName(layoutName);
    dispatch(setSelectedAndonLayoutName(layoutName));
  };

  const handleDeleteLayout = async (layoutName: string) => {
    const currentLayouts = an_boardLayouts || [];
    const updatedLayouts = currentLayouts.filter(
      (layout) => layout.name !== layoutName
    );

    const configToSave = {
      ...clientConfig,
      an_boardLayouts: updatedLayouts
    };

    await saveConfig(configToSave);
    dispatch(setClientConfig(configToSave));
    if (selectedLayoutName === layoutName) {
      setWorkingLayout(EMPTY_WORKING_LAYOUT);
      setSavedLayout(null);
      setLoadedLayoutName(null);
      dispatch(setSelectedAndonLayoutName(null));
    }
  };

  const removeWorkstation = (workstationId: string) => {
    setWorkingLayout((currentWorkingLayout) => ({
      ...currentWorkingLayout,
      layout: currentWorkingLayout.layout.filter(
        (boardLayoutItem) => boardLayoutItem.i !== workstationId
      )
    }));
    editSelectionRef.current.closeEditCellMenu();
  };

  const fulfillmentCenterIdForWorkstation = (workstationId: string) =>
    (
      workstationsRef.current?.find(
        (workstation) => workstation.id === workstationId
      ) as WorkstationWithFulfillmentCenter | undefined
    )?.fulfillmentCenterId ??
    siteFulfillmentCenterId ??
    undefined;

  const selectWorkstation = (
    workstationId: string | null,
    fulfillmentCenterIdOverride?: string
  ) => {
    const { selectedGridIndex, editCellMenuAnchorPosition } =
      editSelectionRef.current;
    if (!workstationId && selectedGridIndex != null) {
      removeWorkstation(selectedGridIndex);
    } else if (workstationId && selectedGridIndex != null) {
      const fulfillmentCenterId =
        fulfillmentCenterIdOverride ??
        fulfillmentCenterIdForWorkstation(workstationId);
      setWorkingLayout((currentWorkingLayout) => ({
        ...currentWorkingLayout,
        layout: replaceWorkstationOnLayout(
          currentWorkingLayout.layout,
          selectedGridIndex,
          workstationId,
          fulfillmentCenterId
        )
      }));
    } else {
      const grid = document.querySelector(`.andon-board`);

      if (grid && editCellMenuAnchorPosition && workstationId) {
        const fulfillmentCenterId =
          fulfillmentCenterIdOverride ??
          fulfillmentCenterIdForWorkstation(workstationId);
        setWorkingLayout((currentWorkingLayout) => {
          const closestPositions = findClosestEmptyBoardPosition({
            layoutItems: currentWorkingLayout.layout,
            columns: currentWorkingLayout.dimensions.columns,
            rows: currentWorkingLayout.dimensions.rows,
            boardRect: grid.getBoundingClientRect(),
            anchor: editCellMenuAnchorPosition,
            boardWidth: boardWidth || 0,
            boardHeight: boardHeight || 0,
            gridHeightPercentage: 100 / currentWorkingLayout.dimensions.rows
          });

          if (!closestPositions) {
            return currentWorkingLayout;
          }

          return {
            ...currentWorkingLayout,
            layout: [
              ...currentWorkingLayout.layout,
              createWorkstationLayoutItem({
                workstationId,
                fulfillmentCenterId,
                position: closestPositions,
                rows: currentWorkingLayout.dimensions.rows
              })
            ]
          };
        });
      }
    }

    editSelectionRef.current.closeEditCellMenu();
    editSelectionRef.current.setSelectedGridIndex(null);
  };

  const openAddTextLabelModal = () => {
    pendingTextLabelAnchorRef.current =
      editSelectionRef.current.editCellMenuAnchorPosition;
    setTextLabelModalOpen(true);
  };

  const handleAddTextLabel = (textLabel: BoardTextLabelFields): boolean => {
    const isEditing = editingTextLabel !== null;

    if (isEditing) {
      setWorkingLayout((currentWorkingLayout) => ({
        ...currentWorkingLayout,
        layout: updateTextLayoutItem(currentWorkingLayout.layout, textLabel)
      }));
      setEditingTextLabel(null);
      editSelectionRef.current.closeEditCellMenu();
      pendingTextLabelAnchorRef.current = null;
      return true;
    }

    const grid = document.querySelector(`.andon-board`);
    const textLabelAnchor =
      pendingTextLabelAnchorRef.current ??
      editSelectionRef.current.editCellMenuAnchorPosition;

    if (!grid || !textLabelAnchor) {
      errorToast(t("could not place text label"));
      return false;
    }

    const closestPositions = findClosestEmptyBoardPosition({
      layoutItems: workingLayoutItems,
      columns: boardDimensions.columns,
      rows: boardDimensions.rows,
      boardRect: grid.getBoundingClientRect(),
      anchor: textLabelAnchor,
      boardWidth: boardWidth || 0,
      boardHeight: boardHeight || 0,
      gridHeightPercentage: 100 / boardDimensions.rows
    });

    if (!closestPositions) {
      errorToast(t("could not place text label"));
      return false;
    }

    setWorkingLayout((currentWorkingLayout) => ({
      ...currentWorkingLayout,
      layout: [
        ...currentWorkingLayout.layout,
        createTextLayoutItem({
          textLabel,
          position: closestPositions,
          rows: currentWorkingLayout.dimensions.rows
        })
      ]
    }));
    setEditingTextLabel(null);
    editSelectionRef.current.closeEditCellMenu();
    pendingTextLabelAnchorRef.current = null;
    return true;
  };

  const handleEditTextLabel = (layoutItemId: string) => {
    const textLayoutItem = workingLayoutItems.find(
      (boardLayoutItem): boardLayoutItem is AndonBoardTextLayoutItem =>
        boardLayoutItem.i === layoutItemId && isTextLayoutItem(boardLayoutItem)
    );
    if (textLayoutItem) {
      setEditingTextLabel({
        id: textLayoutItem.i,
        text: textLayoutItem.text,
        backgroundColor: textLayoutItem.backgroundColor,
        textColor: textLayoutItem.textColor,
        fontSize: textLayoutItem.fontSize
      });
      setTextLabelModalOpen(true);
    }
    editSelectionRef.current.closeEditCellMenu();
  };

  const handleDeleteTextLabel = (layoutItemId: string) => {
    setWorkingLayout((currentWorkingLayout) => ({
      ...currentWorkingLayout,
      layout: currentWorkingLayout.layout.filter(
        (boardLayoutItem) => boardLayoutItem.i !== layoutItemId
      )
    }));
    editSelectionRef.current.closeEditCellMenu();
  };

  const workingWorkstationIds = workingLayoutItems
    .filter(isWorkstationLayoutItem)
    .map((boardLayoutItem) => boardLayoutItem.i);

  const minBoardDimensions: BoardDimensions = useMemo(
    () => calculateMinAndonBoardDimensions(workingLayoutItems),
    [workingLayoutItems]
  );

  const handleSetCellBorder = (sides: BorderSide[], color: string) => {
    const { borderTargetCells } = editSelectionRef.current;
    if (borderTargetCells.length === 0) return;
    setWorkingLayout((currentWorkingLayout) =>
      applyCellBordersToWorkingLayout(
        currentWorkingLayout,
        borderTargetCells,
        sides,
        color
      )
    );
  };

  const handlePickWorkstation = (
    workstationId: string,
    fulfillmentCenterId?: string
  ) => {
    if (fulfillmentCenterId) {
      setWorkingLayout((currentWorkingLayout) => ({
        ...currentWorkingLayout,
        fulfillmentCenterIds: [
          ...new Set([
            ...currentWorkingLayout.fulfillmentCenterIds,
            fulfillmentCenterId
          ])
        ]
      }));
    }
    selectWorkstation(workstationId, fulfillmentCenterId);
  };

  const handleAddWorkstations = (workstationIds: string[], fcId?: string) => {
    const fulfillmentCenterId = fcId ?? siteFulfillmentCenterId;
    if (!fulfillmentCenterId) {
      return;
    }
    const selections = workstationIds.map((workstationId) => ({
      workstationId,
      fulfillmentCenterId
    }));

    setWorkingLayout((currentWorkingLayout) => {
      const { layout, dimensions } = addWorkstationsToBoard(
        currentWorkingLayout.layout,
        currentWorkingLayout.dimensions,
        selections
      );
      return {
        ...currentWorkingLayout,
        layout,
        dimensions,
        fulfillmentCenterIds: [
          ...new Set([
            ...currentWorkingLayout.fulfillmentCenterIds,
            fulfillmentCenterId
          ])
        ]
      };
    });
  };

  const handleClearLayout = () => {
    setWorkingLayout((currentWorkingLayout) => ({
      ...currentWorkingLayout,
      layout: [],
      cellBorders: []
    }));
  };

  const handleDiscardChanges = useCallback(() => {
    if (savedLayout) {
      setWorkingLayout(savedLayout);
      return;
    }

    setWorkingLayout(EMPTY_WORKING_LAYOUT);
    setSavedLayout(null);
    dispatch(setSelectedAndonLayoutName(null));
    setLoadedLayoutName(null);
  }, [savedLayout, dispatch]);

  const closeTextLabelModal = () => {
    setTextLabelModalOpen(false);
    setEditingTextLabel(null);
    pendingTextLabelAnchorRef.current = null;
    editSelectionRef.current.closeEditCellMenu();
  };

  return {
    workingLayout,
    setWorkingLayout,
    savedLayout,
    workingLayoutItems,
    boardDimensions,
    boardScale,
    boardDisplayLines,
    cellBorders,
    workingLayoutName,
    workingLayoutFcIds,
    hasLayoutChanges,
    loadWorkingLayout,
    handleDimensionChange,
    handleSaveLayout,
    handleDeleteLayout,
    handleDiscardChanges,
    handleClearLayout,
    removeWorkstation,
    selectWorkstation,
    handlePickWorkstation,
    handleAddWorkstations,
    editingTextLabel,
    textLabelModalOpen,
    openAddTextLabelModal,
    handleAddTextLabel,
    handleEditTextLabel,
    handleDeleteTextLabel,
    closeTextLabelModal,
    handleSetCellBorder,
    workingWorkstationIds,
    minBoardDimensions
  };
}
